/**
 * Read-only modal showing a client's high-risk intake screening answers (the six yes/no
 * questions captured at intake) for a screening-stage tracker row.
 */

import { Badge, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { useSearchResources } from '@medplum/react-hooks';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  INTAKE_SCREENING_QUESTIONNAIRE_URL,
  SCREENING_QUESTIONS,
} from '../../public/intake/intakeFhirConstants';

export interface ScreeningResultsModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string | null;
  clientName?: string;
}

function answerForLinkId(qr: QuestionnaireResponse, linkId: string): boolean | null {
  const value = qr.item?.find((i) => i.linkId === linkId)?.answer?.[0]?.valueBoolean;
  return value == null ? null : value;
}

function answerLabel(answer: boolean | null): string {
  if (answer == null) return 'No answer';
  return answer ? 'Yes' : 'No';
}

function ScreeningResultsContent({ patientId }: { patientId: string }): JSX.Element {
  const [responses] = useSearchResources('QuestionnaireResponse', {
    subject: `Patient/${patientId}`,
    questionnaire: INTAKE_SCREENING_QUESTIONNAIRE_URL,
    _sort: '-_lastUpdated',
    _count: '1',
  });

  if (responses === undefined) {
    return (
      <Group justify="center" py="lg">
        <Loader size="sm" />
      </Group>
    );
  }

  const qr = responses[0];
  if (!qr) {
    return (
      <Text size="sm" c="dimmed">
        No screening responses on file for this client.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      {SCREENING_QUESTIONS.map((q) => {
        const answer = answerForLinkId(qr, q.linkId);
        return (
          <Group key={q.linkId} justify="space-between" align="flex-start" wrap="nowrap" gap="md">
            <Text size="sm" style={{ flex: 1 }}>
              {q.label}
            </Text>
            <Badge variant="light" color={answer === true ? 'red' : 'gray'}>
              {answerLabel(answer)}
            </Badge>
          </Group>
        );
      })}
    </Stack>
  );
}

export function ScreeningResultsModal({
  open,
  onClose,
  patientId,
  clientName,
}: ScreeningResultsModalProps): JSX.Element {
  return (
    <Modal opened={open} onClose={onClose} title="High-risk screening results" size="lg">
      <Stack gap="md">
        {clientName && (
          <Text size="sm">
            <strong>Client:</strong> {clientName}
          </Text>
        )}
        {open && patientId ? (
          <ScreeningResultsContent patientId={patientId} />
        ) : (
          <Text size="sm" c="dimmed">
            No client selected.
          </Text>
        )}
      </Stack>
    </Modal>
  );
}
